import React from 'react';
import { RRule } from 'rrule';
import moment from 'moment';

const frequencies = [
	{ name: "Year(s)", val: RRule.YEARLY },
	{ name: "Month(s)", val: RRule.MONTHLY },
	{ name: "Week(s)", val: RRule.WEEKLY },
	{ name: "Day(s)", val: RRule.DAILY },
	{ name: "Hour(s)", val: RRule.HOURLY },
	{ name: "Minute(s)", val: RRule.MINUTELY }
];

const days = [ 
	{ name: "Monday", val: 'MO' },
	{ name: "Tuesday", val: 'TU'},
	{ name: "Wednesday", val: 'WE'},
	{ name: "Thursday", val: 'TH'},
	{ name: "Friday", val: 'FR'},
	{ name: "Saturday", val: 'SA'},
	{ name: "Sunday", val: 'SU'}
];

const RRuleEditor = ({ value, onChange, numeric }) => {
	const [state, setState] = React.useState(() => {
		if (value && value.state) {
			const loaded = {
				freq: 1,
				dtstart: '',
				until: '',
				count: 0,
				interval: 1,
				wkst: 0,
				byweekday: [],
				bymonth: [],
				...value.state
			};
			loaded.freq = Number(loaded.freq) || 1;
			delete loaded.dtstart;  // ignore dtstart from stored
			return loaded;
		}
		return {
			freq: 1,
			dtstart: '',
			until: '',
			count: 0,
			interval: 1,
			wkst: 0,
			byweekday: [],
			bymonth: []
		};
	});
	const [txt, setTxt] = React.useState("");
	const [open, setOpen] = React.useState(false);
	const [runmode, setRunMode] = React.useState(0);
	const initialLoadDone = React.useRef(false);

	const toggleByDay = (e) => {
		const val = e.target.value;
		const index = state.byweekday.indexOf(val);
		let copy = [...state.byweekday];
		if (index === -1) {
			copy.push(val);
		} else {
			copy.splice(index, 1);
		}
		setState({ ...state, byweekday: copy });
	};

	const toggleByMonth = (e) => {
		const val = +e.target.value;
		const index = state.bymonth.indexOf(val);
		let copy = [...state.bymonth];
		if (index === -1) {
			copy.push(val);
		} else {
			copy.splice(index, 1);
		}
		setState({ ...state, bymonth: copy });
	};

	const handleRunMode = (e) => {
		setRunMode(e.target.value);
	};

	React.useEffect(() => {
		const rule = { ...state };
		rule.freq = Number(rule.freq) || 1;
		delete rule.runmode; // UI only
		delete rule.dtstart;  // dtstart from stored r_rule should be ignored when instantiating the rrule object; use performAt separately
		if (!state.byweekday.length) delete rule.byweekday;
		if (!state.bymonth.length) delete rule.bymonth;
		if (state.count > 0) {
			delete rule.until;
			setRunMode(2);
		} else {
			delete rule.count;
			if (state.until) {
				rule.until = new Date(state.until);
				setRunMode(1);
			} else {
				delete rule.until;
				setRunMode(0);
			}
		}
		try {
			const rruleObj = new RRule(rule);
			setTxt(rruleObj.toText());
			// Avoid pushing a default rrule on initial mount for new schedules (no value).
			// For loaded value, the parent's lazy init handles it; we just mark done.
			if (onChange) {
				if (initialLoadDone.current) {
					onChange({ state: rule });
				} else {
					initialLoadDone.current = true;
					if (value) {
						// loaded case, don't emit default
					} else {
						// new schedule: do not emit the default monthly rrule
					}
				}
			}
		} catch (e) {}
	}, [state]);

	React.useEffect(() => {
		if (value && value.state) {
			setState(prev => {
				const merged = { ...prev, ...value.state };
				merged.freq = Number(merged.freq) || 1;
				delete merged.dtstart;  // ignore dtstart from stored
				return merged;
			});
		}
	}, [value]);

	const generateFrequencyList = () => frequencies.map((val, i) => (
		<option key={i} value={String(val.val)}>{val.name}</option>
	));

	const generateWeekStart = () => days.map((val, i) => (
		<option key={i} value={val.val}>{val.name}</option>
	));

	return (
		<div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-white dark:bg-gray-900">
			{!open && (
				<div>
					<div className="text-sm mb-2 text-gray-600 dark:text-gray-400">Repeat: <span className="font-medium text-gray-800 dark:text-gray-200">{txt || 'Not set'}</span></div>
					<button 
						onClick={() => setOpen(true)} 
						className="text-sm px-3 py-1 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg"
					>
						Edit Repeat Rule
					</button>
				</div>
			)}
			{open && (
				<div className="space-y-4">
					<div>
						<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Repeat Every</label>
						<div className="flex gap-2">
							<input 
								type="number" 
								className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 w-20 text-sm" 
								value={state.interval} 
								onChange={e => setState(prev => ({ ...prev, interval: +e.target.value }))} 
							/>
							<select 
								className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm flex-1" 
								value={String(state.freq)} 
								onChange={e => setState(prev => ({ ...prev, freq: +e.target.value }))} 
							>
								{generateFrequencyList()}
							</select>
						</div>
					</div>

					<div>
						<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Only on these days</label>
						<div className="grid grid-cols-4 gap-2 text-sm">
							{days.map(d => (
								<label key={d.val} className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600">
									<input type="checkbox" value={d.val} checked={state.byweekday.includes(d.val)} onChange={toggleByDay} />
									<span className="text-xs">{d.name}</span>
								</label>
							))}
						</div>
					</div>

					<div>
						<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Duration</label>
						<select 
							className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm w-full" 
							value={runmode} 
							onChange={handleRunMode}
						>
							<option value={0}>Until Stopped</option>
							<option value={1}>Until Date</option>
							<option value={2}>Count</option>
						</select>
					</div>

					{runmode === 1 && (
						<div>
							<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">End Date</label>
							<input 
								type="date" 
								className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm w-full" 
								value={state.until ? (typeof state.until === 'string' ? state.until.substring(0,10) : state.until.toISOString ? state.until.toISOString().substring(0,10) : '') : ''} 
								onChange={e => setState({ ...state, until: e.target.value })} 
							/>
						</div>
					)}

					<div className="pt-2">
						<button 
							onClick={() => setOpen(false)} 
							className="w-full text-center py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 active:bg-gray-300 dark:active:bg-gray-600 text-sm font-medium rounded-xl transition-colors"
						>
							Done
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default RRuleEditor;